import { FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { backendUrl } from 'utils/constants/url';
import { getTokenFromLocalStorage, getTokenTypeFromLocalStorage } from 'utils/functions/getToken';
import { TAuthResponse } from './types/AuthTypes';

export const baseQuery = fetchBaseQuery({
  baseUrl: backendUrl,
  prepareHeaders(headers) {
    headers.set('Authorization', `${getTokenTypeFromLocalStorage()} ${getTokenFromLocalStorage()}`);
  },
});

const tokenBaseQuery = fetchBaseQuery({
  baseUrl: backendUrl,
});

type RefreshData = {
  data?: TAuthResponse;
  error?: FetchBaseQueryError;
};

export const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('token_type');
  localStorage.removeItem('refresh_token');
};

// Один обмен refresh-токена на все запросы, получившие 401 одновременно: Keycloak ротирует
// refresh-токен, и второй параллельный обмен тем же токеном заканчивался выходом из кабинета.
let refreshInFlight: Promise<boolean> | null = null;

const refreshSession = (api: Parameters<BaseQueryFn>[1], extraOptions: {}): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return false;
      const token = (await tokenBaseQuery(
        { url: 'auth/token/refresh/', method: 'POST', body: { refresh_token: refreshToken } },
        api,
        extraOptions,
      )) as RefreshData;
      if (token.data?.access_token && token.data.refresh_token && token.data.token_type) {
        localStorage.setItem('token', token.data.access_token);
        localStorage.setItem('token_type', token.data.token_type);
        localStorage.setItem('refresh_token', token.data.refresh_token);
        return true;
      }
      // сетевой сбой во время обмена — сессию не трогаем, запрос просто вернёт ошибку
      const status = token.error?.status;
      if (status === 400 || status === 401 || status === 403) {
        clearSession();
        window.location.href = '/';
      }
      return false;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

export const getBaseQueryWithReauth = (
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  return async function (args, api, extraOptions) {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && (result.error.status === 401 || result.error.data === 'Unauthorized')) {
      if (await refreshSession(api, extraOptions)) {
        result = await baseQuery(args, api, extraOptions);
      }
    }
    return result;
  };
};
